import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { Calculator, Save, AlertCircle, CheckCircle2, Calendar, Banknote, Receipt, Sparkles, Scale, TrendingDown, Printer, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => v != null ? new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v) : "—";
const fmt2 = (v) => v != null ? new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v) : "—";
const pct = (v) => v != null ? `${(v * 100).toFixed(2).replace(/\.?0+$/, '')}%` : "—";

const CASSE = [
  { value: "gestione_separata", label: "INPS Gestione Separata", auto: true },
  { value: "artigiani", label: "INPS Artigiani", auto: true },
  { value: "commercianti", label: "INPS Commercianti", auto: true },
  { value: "inarcassa", label: "Inarcassa (Ingegneri/Architetti)", auto: false },
  { value: "cassa_forense", label: "Cassa Forense (Avvocati)", auto: false },
  { value: "cassa_commercialisti", label: "Cassa Commercialisti", auto: false },
  { value: "cassa_biologi", label: "ENPAB (Biologi)", auto: false },
  { value: "enpap", label: "ENPAP (Psicologi)", auto: false },
  { value: "enpam", label: "ENPAM (Medici)", auto: false },
  { value: "enpacl", label: "ENPACL (Consulenti del lavoro)", auto: false },
  { value: "cassa_geometri", label: "Cassa Geometri", auto: false },
  { value: "altra", label: "Altra cassa", auto: false },
];

export const ForfettarioPage = ({ clientId, clientName, consultantName }) => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(2025);
  const [data, setData] = useState({
    incassato: "",
    ateco: "",
    aliquota_imposta: 0.15,
    contributi_pagati_anno: "",
    acconti_versati: "",
    cassa_previdenza: "gestione_separata",
    has_other_employment: false,
    riduzione_35_percento: false,
    note: "",
  });
  const [calc, setCalc] = useState(null);
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savedYears, setSavedYears] = useState([]);

  // ── What-if confronto regimi ──
  const [scenarios, setScenarios] = useState(null);
  const [scLoading, setScLoading] = useState(false);
  const [compensoSrl, setCompensoSrl] = useState(2500);
  const [rimborsoKmSrl, setRimborsoKmSrl] = useState(9000);
  const [costiAmminSrl, setCostiAmminSrl] = useState(3352); // commercialista + paghe + diritti CCIAA
  const [contabForf, setContabForf] = useState(800);
  const [contabSemp, setContabSemp] = useState(2000);
  const [macroclassi, setMacroclassi] = useState([]);
  // Griglia costi: ogni voce ha {label, mode: 'eur'|'pct', value, periodicity: 'monthly'|'annual'}
  const DEFAULT_COSTS = [
    { label: 'Affitto locale', value: '', mode: 'eur', periodicity: 'monthly' },
    { label: 'Utenze (luce, gas, acqua)', value: '', mode: 'eur', periodicity: 'monthly' },
    { label: 'Telefono e internet', value: '', mode: 'eur', periodicity: 'monthly' },
    { label: 'Materiali / prodotti di consumo', value: '', mode: 'pct', periodicity: 'annual' },
    { label: 'Pubblicità e social', value: '', mode: 'eur', periodicity: 'annual' },
    { label: 'Carburanti / km', value: '', mode: 'eur', periodicity: 'annual' },
    { label: 'Assicurazioni', value: '', mode: 'eur', periodicity: 'annual' },
    { label: 'Manutenzioni e riparazioni', value: '', mode: 'eur', periodicity: 'annual' },
    { label: 'Cancelleria / piccole spese', value: '', mode: 'eur', periodicity: 'annual' },
    { label: '', value: '', mode: 'eur', periodicity: 'annual' },
    { label: '', value: '', mode: 'eur', periodicity: 'annual' },
  ];
  const [costRows, setCostRows] = useState(DEFAULT_COSTS);
  const [showReport, setShowReport] = useState(false);

  // Carica macroclassi all'apertura
  useEffect(() => {
    axios.get(`${API}/forfettario/macroclassi`)
      .then(res => setMacroclassi(res.data.macroclassi || []))
      .catch(() => {});
  }, []);

  // Carica la griglia costi salvata per il cliente
  useEffect(() => {
    if (!clientId) return;
    axios.get(`${API}/clients/${clientId}/forfettario-cost-grid`)
      .then(res => {
        const saved = res.data?.data;
        if (saved) {
          if (Array.isArray(saved.cost_rows) && saved.cost_rows.length > 0) {
            setCostRows(saved.cost_rows);
          }
          if (saved.contabilita_forfettario != null) setContabForf(saved.contabilita_forfettario);
          if (saved.contabilita_semplificata != null) setContabSemp(saved.contabilita_semplificata);
          if (saved.compenso_amministratore_mensile != null) setCompensoSrl(saved.compenso_amministratore_mensile);
          if (saved.rimborso_km_annuo != null) setRimborsoKmSrl(saved.rimborso_km_annuo);
          if (saved.costi_amministrativi_srl != null) setCostiAmminSrl(saved.costi_amministrativi_srl);
        }
      })
      .catch(() => {});
  }, [clientId]);

  // Totale annuo costi (riepilogo live)
  const totalCostsAnnual = costRows.reduce((acc, r) => {
    const v = parseFloat(r.value) || 0;
    if (!v) return acc;
    const isPct = r.mode === 'pct';
    const baseValue = isPct ? ((parseFloat(data.incassato) || 0) * v / 100) : v;
    const annualized = r.periodicity === 'monthly' ? baseValue * 12 : baseValue;
    return acc + annualized;
  }, 0);

  const updateCostRow = (idx, patch) => {
    setCostRows(rows => rows.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };
  const addCostRow = () => setCostRows(rows => [...rows, { label: '', value: '', mode: 'eur', periodicity: 'annual' }]);
  const removeCostRow = (idx) => setCostRows(rows => rows.filter((_, i) => i !== idx));

  const runScenarios = async () => {
    setScLoading(true);
    try {
      const costi_dettagliati = costRows
        .filter(r => r.label && (parseFloat(r.value) || 0) > 0)
        .map(r => ({
          label: r.label,
          amount: r.mode === 'eur' ? (parseFloat(r.value) || 0) : 0,
          pct_of_revenue: r.mode === 'pct' ? (parseFloat(r.value) || 0) : 0,
          use_pct: r.mode === 'pct',
          periodicity: r.periodicity,
        }));
      // Persisti la griglia costi e i parametri SRL nel cliente
      const grid_payload = {
        cost_rows: costRows,
        contabilita_forfettario: parseFloat(contabForf) || 0,
        contabilita_semplificata: parseFloat(contabSemp) || 0,
        compenso_amministratore_mensile: parseFloat(compensoSrl) || 0,
        rimborso_km_annuo: parseFloat(rimborsoKmSrl) || 0,
        costi_amministrativi_srl: parseFloat(costiAmminSrl) || 0,
      };
      axios.post(`${API}/clients/${clientId}/forfettario-cost-grid`, grid_payload).catch(() => {});
      const payload = {
        incassato: parseFloat(data.incassato) || 0,
        costi_annuali: totalCostsAnnual,
        costi_dettagliati,
        costo_contabilita_forfettario: parseFloat(contabForf) || 0,
        costo_contabilita_semplificata: parseFloat(contabSemp) || 0,
        ateco: data.ateco,
        aliquota_imposta: parseFloat(data.aliquota_imposta),
        contributi_pagati_anno: parseFloat(data.contributi_pagati_anno) || 0,
        cassa_previdenza: data.cassa_previdenza,
        has_other_employment: data.has_other_employment,
        riduzione_35_percento: data.riduzione_35_percento,
        addizionali_locali_pct: 3.0,
        compenso_amministratore_mensile: compensoSrl,
        rimborso_km_annuo: parseFloat(rimborsoKmSrl) || 0,
        costi_amministrativi_srl: parseFloat(costiAmminSrl) || 0,
        coefficiente_redditivita_override: data.coefficiente_redditivita_override || null,
      };
      const res = await axios.post(`${API}/clients/${clientId}/forfettario/${year}/scenarios`, payload);
      setScenarios(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore confronto scenari");
    } finally {
      setScLoading(false);
    }
  };

  const loadYearData = useCallback(async (y) => {
    if (!clientId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/clients/${clientId}/forfettario/${y}`);
      const fetched = res.data?.data || {};
      setData((prev) => ({
        ...prev,
        ...fetched,
        incassato: fetched.incassato ?? "",
        contributi_pagati_anno: fetched.contributi_pagati_anno ?? "",
        acconti_versati: fetched.acconti_versati ?? "",
      }));
      setExists(res.data?.exists);
      // Always re-run calc (even on prefill) so the user sees current numbers
      if (res.data?.exists) {
        const calcRes = await axios.get(`${API}/clients/${clientId}/forfettario/${y}/calc`);
        setCalc(calcRes.data);
      } else {
        setCalc(null);
      }
    } catch (e) {
      toast.error("Errore caricamento dati");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const loadSavedYears = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await axios.get(`${API}/clients/${clientId}/forfettario`);
      setSavedYears(res.data || []);
    } catch (e) { /* */ }
  }, [clientId]);

  useEffect(() => { loadYearData(year); }, [year, loadYearData]);
  useEffect(() => { loadSavedYears(); }, [loadSavedYears]);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = {
        ...data,
        incassato: parseFloat(data.incassato) || 0,
        contributi_pagati_anno: parseFloat(data.contributi_pagati_anno) || 0,
        acconti_versati: parseFloat(data.acconti_versati) || 0,
        aliquota_imposta: parseFloat(data.aliquota_imposta),
      };
      const res = await axios.post(`${API}/clients/${clientId}/forfettario/${year}`, payload);
      setCalc(res.data?.calc);
      setExists(true);
      toast.success(`Dati ${year} salvati`);
      loadSavedYears();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setLoading(false);
    }
  };

  const cassaInfo = CASSE.find(c => c.value === data.cassa_previdenza);
  const isAutoCalc = cassaInfo?.auto;

  return (
    <div className="space-y-4" data-testid="forfettario-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Calculator className="h-5 w-5 text-violet-700" /> Pianificazione Fiscale Forfettario
          </h2>
          <p className="text-xs text-muted-foreground">
            {clientName} · L. 190/2014 · imposta sostitutiva su (incassato × coefficiente − contributi)
          </p>
        </div>
        <div className="flex items-end gap-2">
          {calc && (
            <Button
              variant="outline"
              onClick={() => setShowReport(true)}
              className="border-violet-300 text-violet-700 hover:bg-violet-50"
              data-testid="forfettario-print-report-btn"
            >
              <Printer className="h-4 w-4 mr-2" />
              Stampa report
            </Button>
          )}
          <div>
            <Label className="text-xs">Anno</Label>
            <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
              <SelectTrigger className="w-32" data-testid="forfettario-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026, 2027].map(y => (
                  <SelectItem key={y} value={String(y)}>
                    {y} {savedYears.find(s => s.year === y) ? "✓" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* INPUT FORM */}
        <Card data-testid="forfettario-input-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Banknote className="h-4 w-4 text-emerald-700" />
              Dati anno {year}
            </CardTitle>
            <CardDescription>
              {exists ? "Modifica i dati e clicca Calcola" : (savedYears.length > 0 ? "Pre-compilato dall'anno precedente" : "Inserisci i dati a mano per il primo anno")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Incassato annuo (€)</Label>
                <Input type="number" value={data.incassato} onChange={(e) => update('incassato', e.target.value)} placeholder="es. 35000" data-testid="forfettario-incassato" />
              </div>
              <div>
                <Label className="text-xs">Tipologia attività (coefficiente redditività)</Label>
                <Select
                  value={data.coefficiente_redditivita_override ? String(data.coefficiente_redditivita_override) : (data.ateco || 'custom')}
                  onValueChange={(v) => {
                    if (v === 'custom') {
                      update('coefficiente_redditivita_override', null);
                    } else {
                      const mc = macroclassi.find(m => String(m.value) === v);
                      if (mc) {
                        setData(d => ({ ...d, coefficiente_redditivita_override: mc.value, macroclasse_label: mc.label, ateco: '' }));
                      }
                    }
                  }}
                >
                  <SelectTrigger data-testid="forfettario-macroclasse"><SelectValue placeholder="Scegli la categoria..." /></SelectTrigger>
                  <SelectContent>
                    {macroclassi.map((mc, i) => (
                      <SelectItem key={i} value={String(mc.value)}>
                        {Math.round(mc.value * 100)}% — {mc.label}
                      </SelectItem>
                    ))}
                    <SelectItem value="custom">Codice ATECO specifico (inserisco io)</SelectItem>
                  </SelectContent>
                </Select>
                {data.coefficiente_redditivita_override && (
                  <p className="text-[10px] text-violet-700 mt-0.5">
                    {macroclassi.find(m => m.value === data.coefficiente_redditivita_override)?.examples}
                  </p>
                )}
                {!data.coefficiente_redditivita_override && (
                  <div className="mt-1">
                    <Input value={data.ateco} onChange={(e) => update('ateco', e.target.value)} placeholder="Codice ATECO es. 86.90.30" data-testid="forfettario-ateco" className="h-8 text-xs" />
                  </div>
                )}
                {calc?.reddito?.ateco_group && (
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {calc.reddito.ateco_group} · coef. {pct(calc.reddito.coefficiente_redditivita)}
                  </p>
                )}
              </div>
            </div>

            <div>
              <Label className="text-xs">Aliquota imposta sostitutiva</Label>
              <Select value={String(data.aliquota_imposta)} onValueChange={(v) => update('aliquota_imposta', parseFloat(v))}>
                <SelectTrigger data-testid="forfettario-aliquota"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0.05">5% — Nuova attività (primi 5 anni)</SelectItem>
                  <SelectItem value="0.15">15% — Aliquota standard</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Contributi previdenziali pagati nell'anno (€)</Label>
                <Input type="number" value={data.contributi_pagati_anno} onChange={(e) => update('contributi_pagati_anno', e.target.value)} placeholder="es. 3500" data-testid="forfettario-contributi" />
                <p className="text-[10px] text-muted-foreground mt-0.5">Deducibili dal reddito imponibile</p>
              </div>
              <div>
                <Label className="text-xs">Acconti imposta versati nell'anno (€)</Label>
                <Input type="number" value={data.acconti_versati} onChange={(e) => update('acconti_versati', e.target.value)} placeholder="es. 0" data-testid="forfettario-acconti" />
                <p className="text-[10px] text-muted-foreground mt-0.5">Saldo + acconti dovuti per l'anno {year}</p>
              </div>
            </div>

            {/* CASSA */}
            <div className="border-t pt-3">
              <Label className="text-xs">Cassa previdenziale</Label>
              <Select value={data.cassa_previdenza} onValueChange={(v) => update('cassa_previdenza', v)}>
                <SelectTrigger data-testid="forfettario-cassa"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CASSE.map(c => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label} {c.auto && "✨"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                ✨ = il sistema calcola minimi ed eccedenti. Per le casse private l'aliquota la conosce solo la cassa stessa.
              </p>
            </div>

            {data.cassa_previdenza === 'gestione_separata' && (
              <div className="flex items-start gap-2 bg-slate-50 rounded p-2">
                <Switch checked={data.has_other_employment} onCheckedChange={(v) => update('has_other_employment', v)} data-testid="forfettario-other-emp" />
                <div>
                  <Label className="text-xs">Iscritta anche ad altra forma previdenziale obbligatoria (es. dipendente)</Label>
                  <p className="text-[10px] text-muted-foreground">Se sì → 24%, altrimenti 26,07%</p>
                </div>
              </div>
            )}

            {(data.cassa_previdenza === 'artigiani' || data.cassa_previdenza === 'commercianti') && (
              <div className="flex items-start gap-2 bg-violet-50 rounded p-2">
                <Switch checked={data.riduzione_35_percento} onCheckedChange={(v) => update('riduzione_35_percento', v)} data-testid="forfettario-rid-35" />
                <div>
                  <Label className="text-xs">Riduzione 35% contributiva (forfettario)</Label>
                  <p className="text-[10px] text-muted-foreground">Solo se hai presentato domanda all'INPS entro il 28/02 dell'anno</p>
                </div>
              </div>
            )}

            <Button onClick={handleSave} disabled={loading} className="w-full" data-testid="forfettario-save">
              <Save className="h-4 w-4 mr-2" />
              {loading ? "Calcolo..." : "Salva e calcola"}
            </Button>
          </CardContent>
        </Card>

        {/* RESULTS */}
        {calc ? (
          <Card className="border-violet-200" data-testid="forfettario-result-card">
            <CardHeader className="pb-3 bg-gradient-to-br from-violet-50 to-blue-50">
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-violet-700" /> Calcolo {year}
              </CardTitle>
              <CardDescription>Reddito imponibile e imposta sostitutiva</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 pt-3">
              {/* REDDITO */}
              <RowKv label={`Incassato annuo`} val={fmt(calc.reddito.incassato)} />
              <RowKv label={`× Coef. redditività ${calc.reddito.ateco_group || ''}`} val={pct(calc.reddito.coefficiente_redditivita)} sub />
              <RowKv label="= Reddito lordo forfettario" val={fmt(calc.reddito.reddito_lordo_forfettario)} bold />
              <RowKv label="− Contributi pagati (deducibili)" val={`− ${fmt(calc.reddito.contributi_pagati_dedotti)}`} sub />
              <RowKv label="= Reddito imponibile" val={fmt(calc.reddito.reddito_imponibile)} bold accent="emerald" />

              <div className="border-t pt-2">
                <RowKv label={`Imposta sostitutiva (${pct(calc.imposta.aliquota)})`} val={fmt(calc.imposta.imposta_sostitutiva)} bold accent="violet" />
                {calc.imposta.acconti_versati_anno > 0 && (
                  <RowKv label="− Acconti già versati" val={`− ${fmt(calc.imposta.acconti_versati_anno)}`} sub />
                )}
                <RowKv label="= Saldo da versare" val={fmt(calc.imposta.saldo_dovuto)} bold accent={calc.imposta.saldo_dovuto > 0 ? "red" : "emerald"} />
              </div>

              {/* ACCONTI ANNO PROSSIMO */}
              {calc.acconti_anno_prossimo.totale > 0 && (
                <div className="border-t pt-2">
                  <div className="text-xs font-semibold text-slate-600 mb-1">Acconti per il {year + 1}</div>
                  {calc.acconti_anno_prossimo.acconto_unico > 0 && (
                    <RowKv label="Acconto unico (novembre)" val={fmt(calc.acconti_anno_prossimo.acconto_unico)} />
                  )}
                  {calc.acconti_anno_prossimo.acconto_giugno > 0 && (
                    <RowKv label="1° acconto (giugno)" val={fmt(calc.acconti_anno_prossimo.acconto_giugno)} />
                  )}
                  {calc.acconti_anno_prossimo.acconto_novembre > 0 && (
                    <RowKv label="2° acconto (novembre)" val={fmt(calc.acconti_anno_prossimo.acconto_novembre)} />
                  )}
                </div>
              )}

              {/* CONTRIBUTI */}
              <div className="border-t pt-2">
                <div className="text-xs font-semibold text-slate-600 mb-1">Contributi previdenziali</div>
                <div className="text-[11px] text-slate-500 italic mb-1">{calc.contributi.metodo}</div>
                {isAutoCalc && (
                  <>
                    {calc.contributi.minimale_dovuto > 0 && (
                      <RowKv label={`Minimale fisso (${calc.contributi.minimale_reddito ? fmt(calc.contributi.minimale_reddito) + ' × ' + pct(calc.contributi.aliquota_applicata) : ''})`} val={fmt(calc.contributi.minimale_dovuto)} />
                    )}
                    {calc.contributi.eccedente_dovuto > 0 && (
                      <RowKv label={`Eccedente sopra minimale`} val={fmt(calc.contributi.eccedente_dovuto)} />
                    )}
                    {calc.contributi.addizionale_oltre_soglia > 0 && (
                      <RowKv label={`IVS aggiuntivo 1% sopra soglia`} val={fmt(calc.contributi.addizionale_oltre_soglia)} />
                    )}
                  </>
                )}
                <RowKv label="Totale contributi anno" val={fmt(calc.contributi.totale_anno)} bold accent="amber" />
                {calc.contributi.note && (
                  <div className="text-[10px] text-slate-500 italic mt-1">ℹ️ {calc.contributi.note}</div>
                )}
              </div>

              {/* TOTALE */}
              <div className="border-t pt-2 bg-slate-50 -mx-6 px-6 py-2">
                <RowKv label="Totale da versare nell'anno" val={fmt(calc.totale_anno.totale_versare)} bold accent="violet" big />
                <div className="text-[10px] text-slate-500 mt-0.5">
                  Imposta {fmt(calc.totale_anno.imposta)} + Contributi {fmt(calc.totale_anno.contributi)}
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-slate-50 border-dashed" data-testid="forfettario-empty">
            <CardContent className="py-12 text-center">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 text-slate-400" />
              <p className="text-sm text-muted-foreground">
                Inserisci i dati e clicca <strong>Salva e calcola</strong> per vedere imposta, acconti e contributi.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* F24 SCHEDULE */}
      {calc && calc.f24 && calc.f24.length > 0 && (
        <Card data-testid="forfettario-f24-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Receipt className="h-4 w-4 text-blue-700" />
              Riepilogo importi e scadenze
            </CardTitle>
            <CardDescription>Calendario dei versamenti F24 per l'anno {year} e gli acconti {year + 1}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-2">
              {calc.f24.map((row, i) => (
                <div key={i} className="bg-slate-50 rounded-md p-3 border border-slate-200" data-testid={`forfettario-f24-row-${i}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-semibold text-slate-700 leading-tight">{row.descrizione}</div>
                    <div className={`text-sm font-bold tabular-nums ${row.importo >= 0 ? 'text-slate-900' : 'text-emerald-700'}`}>
                      {fmt2(row.importo)}
                    </div>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> {row.scadenza}
                  </div>
                </div>
              ))}
            </div>
            {savedYears.length === 0 && exists && (
              <div className="mt-3 text-[11px] text-slate-500 italic flex items-start gap-1">
                <CheckCircle2 className="h-3 w-3 mt-0.5 text-emerald-600" />
                Dati salvati. L'anno prossimo questi valori verranno suggeriti automaticamente come acconti versati.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* SCENARIO COMPARISON: Forfettario vs Ordinario vs SRL */}
      {calc && (
        <Card className="border-2 border-violet-200" data-testid="forfettario-scenarios-card">
          <CardHeader className="pb-3 bg-gradient-to-r from-violet-50 to-blue-50 rounded-t-lg">
            <CardTitle className="text-base flex items-center gap-2">
              <Scale className="h-4 w-4 text-violet-700" />
              Confronto regimi: Forfettario vs Semplificato vs SRL
            </CardTitle>
            <CardDescription>
              Inserisci i costi reali e vedi quale regime ti conviene di più — utile per decidere se rimanere forfettari, passare al Semplificato o aprire una SRL.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 pt-3">
            {/* ── Griglia dei costi sostenuti ── */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="forfettario-cost-grid">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <Label className="text-sm font-semibold">Costi sostenuti</Label>
                  <p className="text-[10px] text-muted-foreground">Inserisci valore in € oppure % sul fatturato. Indica se mensile o annuale.</p>
                </div>
                <Button variant="outline" size="sm" onClick={addCostRow} data-testid="forfettario-cost-add-row" className="text-xs">
                  + Aggiungi riga
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-slate-600">
                    <tr className="border-b">
                      <th className="text-left font-semibold px-1 py-1.5">Voce</th>
                      <th className="text-center font-semibold px-1 py-1.5 w-[80px]">€ / %</th>
                      <th className="text-right font-semibold px-1 py-1.5 w-[110px]">Valore</th>
                      <th className="text-center font-semibold px-1 py-1.5 w-[100px]">Periodo</th>
                      <th className="text-right font-semibold px-1 py-1.5 w-[100px]">Annuo</th>
                      <th className="w-[24px]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {costRows.map((row, idx) => {
                      const v = parseFloat(row.value) || 0;
                      const isPct = row.mode === 'pct';
                      const baseVal = isPct ? ((parseFloat(data.incassato) || 0) * v / 100) : v;
                      const annualized = row.periodicity === 'monthly' ? baseVal * 12 : baseVal;
                      return (
                        <tr key={idx} className="border-b last:border-b-0">
                          <td className="px-1 py-1">
                            <Input
                              value={row.label}
                              onChange={(e) => updateCostRow(idx, { label: e.target.value })}
                              placeholder="Descrizione"
                              className="h-7 text-xs"
                              data-testid={`forfettario-cost-label-${idx}`}
                            />
                          </td>
                          <td className="px-1 py-1">
                            <Select value={row.mode} onValueChange={(v) => updateCostRow(idx, { mode: v })}>
                              <SelectTrigger className="h-7 text-xs" data-testid={`forfettario-cost-mode-${idx}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="eur">€</SelectItem>
                                <SelectItem value="pct">% fatt.</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-1 py-1">
                            <Input
                              type="number"
                              value={row.value}
                              onChange={(e) => updateCostRow(idx, { value: e.target.value })}
                              placeholder={isPct ? '5' : '0'}
                              className="h-7 text-xs text-right"
                              data-testid={`forfettario-cost-value-${idx}`}
                            />
                          </td>
                          <td className="px-1 py-1">
                            <Select value={row.periodicity} onValueChange={(v) => updateCostRow(idx, { periodicity: v })}>
                              <SelectTrigger className="h-7 text-xs" data-testid={`forfettario-cost-period-${idx}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="monthly">Mensile</SelectItem>
                                <SelectItem value="annual">Annuo</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-1 py-1 text-right tabular-nums text-slate-700">{annualized > 0 ? fmt(annualized) : '—'}</td>
                          <td className="px-1 py-1 text-center">
                            <button type="button" onClick={() => removeCostRow(idx)} className="text-slate-400 hover:text-red-600 text-base leading-none" title="Rimuovi">×</button>
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="bg-blue-50 font-bold text-sm">
                      <td colSpan={4} className="px-1 py-2 text-right">TOTALE COSTI ANNUI:</td>
                      <td className="px-1 py-2 text-right tabular-nums text-blue-700">{fmt(totalCostsAnnual)}</td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Costi contabilità (per il break-even) ── */}
            <div className="grid sm:grid-cols-2 gap-3 items-end">
              <div>
                <Label className="text-xs">Costo contabilità Forfettario (€/anno)</Label>
                <Input type="number" value={contabForf} onChange={(e) => setContabForf(parseFloat(e.target.value) || 0)} data-testid="forfettario-contab-forf" />
                <p className="text-[10px] text-muted-foreground mt-0.5">tipico: 600-1.500 €</p>
              </div>
              <div>
                <Label className="text-xs">Costo contabilità Semplificato (€/anno)</Label>
                <Input type="number" value={contabSemp} onChange={(e) => setContabSemp(parseFloat(e.target.value) || 0)} data-testid="forfettario-contab-semp" />
                <p className="text-[10px] text-muted-foreground mt-0.5">tipico: 1.500-3.500 €</p>
              </div>
            </div>

            {/* ── Parametri SRL ── */}
            <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50/30 p-3" data-testid="forfettario-srl-params">
              <div className="mb-2">
                <Label className="text-sm font-semibold text-emerald-900">Parametri SRL (per la simulazione)</Label>
                <p className="text-[10px] text-muted-foreground">Compila questi campi per confrontare anche lo scenario con SRL.</p>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Compenso amministratore (€/mese)</Label>
                  <Input type="number" value={compensoSrl} onChange={(e) => setCompensoSrl(parseFloat(e.target.value) || 0)} data-testid="forfettario-scenario-compenso" />
                  <p className="text-[10px] text-muted-foreground mt-0.5">tipico: 1.500-3.500 €/mese</p>
                </div>
                <div>
                  <Label className="text-xs">Rimborso km amministratore (€/anno)</Label>
                  <Input type="number" value={rimborsoKmSrl} onChange={(e) => setRimborsoKmSrl(parseFloat(e.target.value) || 0)} data-testid="forfettario-scenario-km" />
                  <p className="text-[10px] text-muted-foreground mt-0.5">non imponibile, deducibile per SRL</p>
                </div>
                <div>
                  <Label className="text-xs">Costi amministrativi SRL (€/anno)</Label>
                  <Input type="number" value={costiAmminSrl} onChange={(e) => setCostiAmminSrl(parseFloat(e.target.value) || 0)} data-testid="forfettario-scenario-costi-ammin" />
                  <p className="text-[10px] text-muted-foreground mt-0.5">commercialista + paghe + diritti CCIAA</p>
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={runScenarios} disabled={scLoading} data-testid="forfettario-scenario-run">
                <Sparkles className="h-4 w-4 mr-2" />
                {scLoading ? "Calcolo..." : "Confronta scenari"}
              </Button>
            </div>

            {scenarios && (
              <div className="space-y-3">
                {/* Verdict bar */}
                <div className="flex items-center justify-center gap-2 bg-emerald-50 border-2 border-emerald-200 rounded-lg p-3" data-testid="forfettario-scenario-verdict">
                  <CheckCircle2 className="h-5 w-5 text-emerald-700" />
                  <div>
                    <div className="text-sm font-bold text-emerald-800">
                      Il regime migliore per te è: <span className="uppercase">{scenarios.migliore === 'ordinario_semplificata' ? 'SEMPLIFICATO' : scenarios.migliore}</span>
                    </div>
                    {scenarios.migliore === 'forfettario' && (
                      <div className="text-xs text-slate-600">
                        Perderesti {fmt(Math.abs(scenarios.risparmio_vs_forfettario.ordinario_semplificata))} passando al Semplificato · {fmt(Math.abs(scenarios.risparmio_vs_forfettario.srl))} passando a SRL
                      </div>
                    )}
                    {scenarios.migliore !== 'forfettario' && (
                      <div className="text-xs text-slate-600">
                        Risparmio: {fmt(Math.abs(scenarios.risparmio_vs_forfettario[scenarios.migliore]))}/anno
                      </div>
                    )}
                  </div>
                </div>

                {/* Tre colonne */}
                <div className="grid md:grid-cols-3 gap-3">
                  <ScenarioCol s={scenarios.forfettario} winner={scenarios.migliore === 'forfettario'} accent="violet" testid="sc-forfettario" />
                  <ScenarioCol s={scenarios.ordinario_semplificata} winner={scenarios.migliore === 'ordinario_semplificata'} accent="blue" testid="sc-ordinario" />
                  <ScenarioCol s={scenarios.srl} winner={scenarios.migliore === 'srl'} accent="emerald" testid="sc-srl" />
                </div>

                {/* ── Break-even Forfettario vs Semplificata ── */}
                {scenarios.break_even && (
                  <div className="rounded-xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 via-white to-yellow-50/40 p-4" data-testid="break-even-card">
                    <h3 className="text-base font-bold text-amber-900 flex items-center gap-2 mb-1">
                      🎯 Punti di pareggio Forfettario ↔ Semplificata
                    </h3>
                    <p className="text-xs text-slate-600 mb-3">
                      A che livello cambia la convenienza tra i due regimi, partendo dai dati attuali (fatturato {fmt(scenarios.break_even.fatturato_riferimento)} · spese {fmt(scenarios.break_even.spese_riferimento)}).
                    </p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {/* Spese break-even */}
                      <div className="rounded-lg border border-amber-200 bg-white p-3">
                        <div className="text-[10px] uppercase text-amber-700 font-bold tracking-wide mb-1">
                          A parità di fatturato ({fmt(scenarios.break_even.fatturato_riferimento)})
                        </div>
                        {scenarios.break_even.costi_breakeven != null ? (
                          <>
                            <div className="text-[11px] text-slate-600">Limite di spese per restare nel Forfettario:</div>
                            <div className="text-3xl font-black text-amber-700 tabular-nums my-1">
                              {fmt(scenarios.break_even.costi_breakeven)}
                            </div>
                            <div className="text-[11px] text-slate-700">
                              {scenarios.break_even.spese_riferimento <= scenarios.break_even.costi_breakeven ? (
                                <>✅ Le tue spese attuali ({fmt(scenarios.break_even.spese_riferimento)}) sono sotto questo limite. <strong>Forfettario conviene.</strong></>
                              ) : (
                                <>⚠️ Le tue spese attuali ({fmt(scenarios.break_even.spese_riferimento)}) <strong>superano</strong> il limite. Meglio la Semplificata.</>
                              )}
                            </div>
                          </>
                        ) : (
                          <div className="text-sm text-emerald-700 italic">
                            ✓ In questo scenario il <strong>Forfettario è sempre più conveniente</strong> qualunque sia il livello di spesa.
                          </div>
                        )}
                      </div>
                      {/* Fatturato break-even */}
                      <div className="rounded-lg border border-amber-200 bg-white p-3">
                        <div className="text-[10px] uppercase text-amber-700 font-bold tracking-wide mb-1">
                          A parità di spese ({fmt(scenarios.break_even.spese_riferimento)})
                        </div>
                        {scenarios.break_even.fatturato_breakeven != null ? (
                          <>
                            <div className="text-[11px] text-slate-600">Soglia minima di fatturato per cui il Forfettario conviene:</div>
                            <div className="text-3xl font-black text-amber-700 tabular-nums my-1">
                              {fmt(scenarios.break_even.fatturato_breakeven)}
                            </div>
                            <div className="text-[11px] text-slate-700">
                              {scenarios.break_even.fatturato_riferimento >= scenarios.break_even.fatturato_breakeven ? (
                                <>✅ Il tuo fatturato attuale è sopra questa soglia. <strong>Forfettario conviene.</strong></>
                              ) : (
                                <>⚠️ Il tuo fatturato è <strong>sotto</strong> la soglia. Meglio la Semplificata fino a {fmt(scenarios.break_even.fatturato_breakeven)}.</>
                              )}
                            </div>
                          </>
                        ) : (
                          <div className="text-sm text-emerald-700 italic">
                            ✓ Il <strong>Forfettario conviene</strong> a qualunque livello di fatturato (entro il limite legale di {fmt(scenarios.break_even.limite_forfettario)}).
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 text-[10px] text-slate-500 italic">
                      ⚖️ Il calcolo tiene già conto del maggior costo della contabilità in semplificata ({fmt(contabSemp)} vs {fmt(contabForf)} €/anno).
                    </div>
                  </div>
                )}

                <div className="text-[11px] text-slate-500 italic">
                  ℹ️ Il calcolo è una stima. Per il Semplificato viene applicato uno standard di addizionali locali del 3%.
                  L'SRL include il compenso amministratore mensile, rimborso km, INPS GS al 26,07%, IRES 24%, IRAP 3,9% e i costi amministrativi indicati.
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── REPORT FULLSCREEN PRINT ── */}
      {showReport && calc && (
        <PrintReport
          clientId={clientId}
          clientName={clientName}
          consultantName={consultantName}
          year={year}
          calc={calc}
          scenarios={scenarios}
          costRows={costRows}
          contabForf={contabForf}
          contabSemp={contabSemp}
          compensoSrl={compensoSrl}
          rimborsoKmSrl={rimborsoKmSrl}
          costiAmminSrl={costiAmminSrl}
          incassato={parseFloat(data.incassato) || 0}
          totalCostsAnnual={totalCostsAnnual}
          onClose={() => setShowReport(false)}
        />
      )}
    </div>
  );
};

const RowKv = ({ label, val, sub, bold, accent, big }) => {
  const accentCls = accent === 'red' ? 'text-red-700'
    : accent === 'emerald' ? 'text-emerald-700'
    : accent === 'violet' ? 'text-violet-800'
    : accent === 'amber' ? 'text-amber-700'
    : 'text-slate-700';
  return (
    <div className={`flex items-center justify-between gap-2 ${sub ? 'pl-3' : ''} ${big ? 'text-base' : 'text-xs'}`}>
      <div className={`${sub ? 'text-slate-500' : 'text-slate-700'}`}>{label}</div>
      <div className={`tabular-nums ${bold ? 'font-bold' : ''} ${accentCls}`}>{val}</div>
    </div>
  );
};

const ScenarioCol = ({ s, winner, accent, testid }) => {
  const accentClass = accent === 'violet' ? 'border-violet-300 bg-violet-50/40'
    : accent === 'blue' ? 'border-blue-300 bg-blue-50/40'
    : 'border-emerald-300 bg-emerald-50/40';
  const incassato = s.incassato || 0;
  const isForfettario = s.imposta_sostitutiva != null;
  const isSemplificato = s.irpef_lorda != null;
  const isSrl = s.ires != null;

  return (
    <div className={`rounded-lg border-2 p-3 ${accentClass} ${winner ? 'ring-2 ring-emerald-500 ring-offset-1' : ''}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-bold text-sm text-slate-800">{s.regime}</h4>
        {winner && <span className="text-[10px] font-bold bg-emerald-600 text-white px-2 py-0.5 rounded-full">MIGLIORE</span>}
      </div>
      <div className="space-y-1 text-xs">
        {/* ── Base imponibile ── */}
        <RowKv label="Incassato" val={fmt(s.incassato)} />

        {isForfettario && (
          <>
            <RowKv label={`× Coef. redditività (${Math.round((s.coefficiente_redditivita || 0) * 100)}%)`} val="" sub />
            <RowKv
              label={`= Reddito lordo (${Math.round((s.coefficiente_redditivita || 0) * 100)}% del fatt.)`}
              val={fmt(s.reddito_lordo)}
              sub
            />
            {s.contributi_dedotti > 0 && <RowKv label="− Contributi dedotti" val={`− ${fmt(s.contributi_dedotti)}`} sub />}
            <RowKv label="= Reddito imponibile" val={fmt(s.reddito_imponibile)} sub bold />
          </>
        )}

        {isSemplificato && (
          <>
            {s.costi_deducibili > 0 && <RowKv label="− Costi deducibili" val={`− ${fmt(s.costi_deducibili)}`} accent="red" sub />}
            <RowKv label="= Reddito lordo" val={fmt(s.reddito_lordo)} sub />
            {s.contributi_dedotti > 0 && <RowKv label="− Contributi dedotti" val={`− ${fmt(s.contributi_dedotti)}`} sub />}
            <RowKv label="= Reddito imponibile" val={fmt(s.reddito_imponibile)} sub bold />
          </>
        )}

        {isSrl && (
          <>
            {s.costi_deducibili > 0 && <RowKv label="− Costi reali deducibili" val={`− ${fmt(s.costi_deducibili)}`} accent="red" sub />}
            {s.compensi_amministratori_annui > 0 && (
              <RowKv label={`− Compenso amm. (${fmt(s.compenso_mensile)}/mese × 12)`} val={`− ${fmt(s.compensi_amministratori_annui)}`} accent="red" sub />
            )}
            {s.inps_azienda_annua > 0 && (
              <RowKv label="− INPS quota azienda (17,38% comp.)" val={`− ${fmt(s.inps_azienda_annua)}`} accent="red" sub />
            )}
            {s.rimborsi_km_totali > 0 && <RowKv label="− Rimborso km" val={`− ${fmt(s.rimborsi_km_totali)}`} accent="red" sub />}
            {s.costi_amministrativi_srl_detail > 0 && (
              <RowKv label="− Costi amministrativi" val={`− ${fmt(s.costi_amministrativi_srl_detail)}`} accent="red" sub />
            )}
            <RowKv label="= Utile ante imposte SRL" val={fmt(s.utile_ante_imposte_srl)} sub bold />
          </>
        )}

        <div className="border-t my-1.5"></div>

        {/* ── DETTAGLIO IMPOSTE ── */}
        <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Imposte</div>
        {isForfettario && (
          <RowKv
            label={`Imposta sostitutiva (${Math.round((s.aliquota_imposta || 0) * 100)}%)`}
            val={fmt(s.imposta_sostitutiva)}
            accent="red"
          />
        )}
        {isSemplificato && (
          <>
            <RowKv label="IRPEF lorda (scaglioni)" val={fmt(s.irpef_lorda)} accent="red" />
            {s.detrazione_lavoro_autonomo > 0 && (
              <RowKv
                label="− Detrazione da lavoro autonomo"
                val={`− ${fmt(s.detrazione_lavoro_autonomo)}`}
                accent="emerald"
                sub
              />
            )}
            <RowKv label="= IRPEF netta" val={fmt(s.irpef_netta)} accent="red" bold />
            <RowKv
              label={`+ Addiz. locali (${(s.addizionali_locali_pct ?? 3).toString().replace('.', ',')}%)`}
              val={fmt(s.addizionali_locali)}
              accent="red"
              sub
            />
          </>
        )}
        {isSrl && (
          <>
            <RowKv label="IRES (24% utile)" val={fmt(s.ires)} accent="red" />
            <RowKv label="IRAP (3,9% base)" val={fmt(s.irap)} accent="red" />
            <RowKv label="IRPEF amministratore" val={fmt(s.irpef_amministratori)} accent="red" />
          </>
        )}

        {/* ── DETTAGLIO CONTRIBUTI ── */}
        <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 pt-1">Contributi</div>
        {(isForfettario || isSemplificato) && (
          <>
            {s.contributi_dettaglio?.minimale_dovuto > 0 && (
              <RowKv label="Minimale fisso INPS" val={fmt(s.contributi_dettaglio.minimale_dovuto)} accent="amber" sub />
            )}
            {s.contributi_dettaglio?.eccedente_dovuto > 0 && (
              <RowKv label="Eccedente sopra minimale" val={fmt(s.contributi_dettaglio.eccedente_dovuto)} accent="amber" sub />
            )}
            {s.contributi_dettaglio?.addizionale_oltre_soglia > 0 && (
              <RowKv label="IVS aggiuntivo 1%" val={fmt(s.contributi_dettaglio.addizionale_oltre_soglia)} accent="amber" sub />
            )}
            <RowKv label="Contributi previdenziali" val={fmt(s.contributi_dovuti)} accent="amber" bold />
          </>
        )}
        {isSrl && (
          <RowKv label="INPS amm. (8,69% compenso)" val={fmt(s.inps_amministratori)} accent="amber" bold />
        )}

        <div className="border-t my-1.5"></div>
        <RowKv label="Imposte + contributi" val={fmt(s.totale_imposte_contributi)} bold />
        {s.costo_contabilita > 0 && <RowKv label="+ Costo contabilità" val={fmt(s.costo_contabilita)} accent="red" sub />}
        {isSrl && s.utile_netto_srl != null && (
          <div className="text-[10px] text-slate-500 italic pt-1">
            Utile netto SRL (in azienda): {fmt(s.utile_netto_srl)}
          </div>
        )}

        {/* ── Costi reali sostenuti: SEMPRE visibili come riga che esce dalla cassa ── */}
        {s.costi_reali_sostenuti > 0 && (
          <div className={`mt-1 px-2 py-1 rounded ${isForfettario ? 'bg-red-50 border border-red-200' : 'bg-slate-50'}`}>
            <RowKv
              label={isForfettario
                ? '− Costi reali (non deducibili, escono dalla cassa)'
                : '− Costi reali (già dedotti fiscalmente, escono comunque dalla cassa)'}
              val={`− ${fmt(s.costi_reali_sostenuti)}`}
              accent="red"
              sub
            />
          </div>
        )}

        <div className="bg-white rounded p-2 mt-1.5 border-2 border-emerald-200">
          <RowKv label="NETTO IN TASCA" val={fmt(s.netto_in_tasca)} bold big accent="emerald" />
          {s.aliquota_effettiva_pct != null && (
            <div className="text-[10px] text-slate-500 text-right mt-0.5">Aliq. effettiva {s.aliquota_effettiva_pct}%</div>
          )}
          {incassato > 0 && (
            <div className="text-[10px] text-slate-500 text-right">= {((s.netto_in_tasca / incassato) * 100).toFixed(1)}% del fatturato</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForfettarioPage;

// ──────────────────────────────────────────────────────────────
// PRINT REPORT — vista fullscreen ottimizzata per la stampa PDF
// ──────────────────────────────────────────────────────────────
const PrintReport = ({
  clientId, clientName, consultantName, year, calc, scenarios, costRows,
  contabForf, contabSemp, compensoSrl, rimborsoKmSrl, costiAmminSrl,
  incassato, totalCostsAnnual, onClose,
}) => {
  const today = new Date().toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
  const [generating, setGenerating] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const handlePrint = () => window.print();

  const handleDownloadPdf = async () => {
    setGenerating(true);
    try {
      // Costi dettagliati per il confronto regimi (vengono ricostruiti dal frontend)
      const costi_dettagliati = (costRows || []).filter(r => r.label && (parseFloat(r.value) || 0) > 0).map(r => ({
        label: r.label,
        amount: r.mode === 'eur' ? (parseFloat(r.value) || 0) : 0,
        pct_of_revenue: r.mode === 'pct' ? (parseFloat(r.value) || 0) : 0,
        use_pct: r.mode === 'pct',
        periodicity: r.periodicity,
      }));
      // Recupero i dati salvati per l'anno per avere tutti i parametri (aliquota, cassa, ATECO, ecc.)
      const yearDataRes = await axios.get(`${API}/clients/${clientId}/forfettario/${year}`);
      const yd = yearDataRes.data?.data || {};
      const reqBody = {
        incassato: parseFloat(yd.incassato) || (calc?.reddito?.incassato || 0),
        ateco: yd.ateco || '',
        aliquota_imposta: parseFloat(yd.aliquota_imposta) || 0.15,
        contributi_pagati_anno: parseFloat(yd.contributi_pagati_anno) || 0,
        acconti_versati: parseFloat(yd.acconti_versati) || 0,
        cassa_previdenza: yd.cassa_previdenza || 'gestione_separata',
        has_other_employment: !!yd.has_other_employment,
        riduzione_35_percento: !!yd.riduzione_35_percento,
        coefficiente_redditivita_override: yd.coefficiente_redditivita_override || null,
        macroclasse_label: yd.macroclasse_label || null,
        costi_dettagliati,
        costo_contabilita_forfettario: parseFloat(contabForf) || 0,
        costo_contabilita_semplificata: parseFloat(contabSemp) || 0,
        compenso_amministratore_mensile: parseFloat(compensoSrl) || 0,
        rimborso_km_annuo: parseFloat(rimborsoKmSrl) || 0,
        costi_amministrativi_srl: parseFloat(costiAmminSrl) || 0,
        addizionali_locali_pct: 3.0,
        include_scenarios: !!scenarios,
        consultant_name: consultantName || null,
      };
      const response = await axios.post(
        `${API}/clients/${clientId}/forfettario/${year}/report-pdf`,
        reqBody,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });

      // Provo download diretto
      const safeName = (clientName || 'cliente').replace(/[^a-zA-Z0-9_-]/g, '_');
      const filename = `Report_Forfettario_${safeName}_${year}.pdf`;
      const blobUrl = URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (_e) { /* fallback */ }

      // Converto in data URI per il viewer (più compatibile dei blob: in iframe sandbox)
      const reader = new FileReader();
      reader.onloadend = () => {
        setPdfUrl(reader.result);
        URL.revokeObjectURL(blobUrl);
      };
      reader.readAsDataURL(blob);
      toast.success('PDF pronto');
    } catch (e) {
      toast.error('Errore generazione PDF: ' + (e.message || e));
    } finally {
      setGenerating(false);
    }
  };

  const closeViewer = () => {
    setPdfUrl(null);
  };

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #forfettario-report-preview, #forfettario-report-preview * { visibility: visible !important; }
          #forfettario-report-preview {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            right: 0 !important;
            width: 100% !important;
            max-width: none !important;
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
            display: block !important;
          }
          .print-no-break { page-break-inside: avoid; }
          .print-page-break { page-break-before: always; }
          .print-hide { display: none !important; }
          .report-overlay { position: static !important; background: white !important; padding: 0 !important; overflow: visible !important; }
          @page { size: A4; margin: 16mm 14mm; }
        }
        #forfettario-print-report { font-family: 'Inter', system-ui, -apple-system, sans-serif; color: #0f172a; }
        #forfettario-print-report h1 { font-size: 22pt; font-weight: 800; letter-spacing: -0.5px; margin: 0; color: #0b1431; }
        #forfettario-print-report h2 { font-size: 13pt; font-weight: 700; margin: 0 0 8px 0; color: #1e293b; padding-bottom: 5px; border-bottom: 2px solid #6d28d9; }
        #forfettario-print-report h3 { font-size: 11pt; font-weight: 700; margin: 0 0 6px 0; color: #475569; }
        #forfettario-print-report .report-section { margin-bottom: 22px; }
        #forfettario-print-report .report-table { width: 100%; border-collapse: collapse; font-size: 9.5pt; margin-top: 6px; }
        #forfettario-print-report .report-table td { padding: 5px 10px; border-bottom: 1px solid #e2e8f0; }
        #forfettario-print-report .report-table td:last-child { text-align: right; font-variant-numeric: tabular-nums; }
        #forfettario-print-report .report-table tr.total td { font-weight: 700; background: #f8fafc; border-bottom: 2px solid #6d28d9; padding-top: 7px; padding-bottom: 7px; }
        #forfettario-print-report .report-table tr.subtotal td { background: #fef3f2; font-weight: 600; }
        #forfettario-print-report .report-table tr.deduction td { color: #166534; }
        #forfettario-print-report .badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 8pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
        #forfettario-print-report .badge-violet { background: #ede9fe; color: #5b21b6; }
        #forfettario-print-report .badge-blue { background: #dbeafe; color: #1e40af; }
        #forfettario-print-report .badge-emerald { background: #d1fae5; color: #065f46; }
        #forfettario-print-report .badge-winner { background: #059669; color: white; }
        #forfettario-print-report .scenario-card { border: 1.5px solid #cbd5e1; border-radius: 8px; padding: 14px; background: #fafafa; }
        #forfettario-print-report .scenario-card.winner { border-color: #059669; border-width: 2.5px; background: #f0fdf4; }
        #forfettario-print-report .scenario-card .report-table td { padding: 4px 8px; }
        #forfettario-print-report .net-pocket { background: white; border: 2px solid #059669; border-radius: 6px; padding: 8px; margin-top: 10px; }
        #forfettario-print-report .net-pocket .label { font-size: 8pt; color: #475569; text-transform: uppercase; letter-spacing: 0.8px; font-weight: 700; }
        #forfettario-print-report .net-pocket .value { font-size: 16pt; font-weight: 800; color: #047857; font-variant-numeric: tabular-nums; }
        #forfettario-print-report .net-pocket .pct { font-size: 8.5pt; color: #64748b; }
        #forfettario-print-report .narrative { font-size: 10pt; line-height: 1.55; color: #1e293b; background: #fefce8; border-left: 4px solid #ca8a04; padding: 12px 14px; border-radius: 4px; }
        #forfettario-print-report .narrative p { margin: 0 0 8px 0; }
        #forfettario-print-report .narrative p:last-child { margin-bottom: 0; }
        #forfettario-print-report .narrative strong { color: #713f12; }
      `}</style>

      {/* Overlay toolbar */}
      <div className="print-hide fixed top-0 left-0 right-0 z-[60] bg-slate-900 text-white px-4 py-2 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-2">
          <Printer className="h-4 w-4" />
          <span className="text-sm font-semibold">Anteprima report — {clientName} · {year}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleDownloadPdf} disabled={generating} className="bg-emerald-600 hover:bg-emerald-700" data-testid="report-download-pdf-btn">
            {generating ? 'Generazione…' : '⬇ Scarica PDF'}
          </Button>
          <Button onClick={handlePrint} variant="outline" className="bg-white text-slate-900 hover:bg-slate-100" data-testid="report-print-btn">
            <Printer className="h-4 w-4 mr-2" /> Stampa
          </Button>
          <Button variant="ghost" onClick={onClose} className="text-white hover:bg-white/10" data-testid="report-close-btn">
            <X className="h-4 w-4 mr-1" /> Chiudi
          </Button>
        </div>
      </div>

      {/* Print sheet */}
      <div className="report-overlay fixed inset-0 z-50 bg-slate-200 overflow-y-auto pt-14 pb-8">
        <div id="forfettario-report-preview" className="max-w-[210mm] mx-auto bg-white shadow-2xl my-4">
          <ReportBody
            clientName={clientName} consultantName={consultantName} year={year} today={today} calc={calc} scenarios={scenarios}
            costRows={costRows} contabForf={contabForf} contabSemp={contabSemp}
            compensoSrl={compensoSrl} rimborsoKmSrl={rimborsoKmSrl} costiAmminSrl={costiAmminSrl}
            incassato={incassato} totalCostsAnnual={totalCostsAnnual}
          />
        </div>
      </div>

      {/* PDF Viewer interno (fallback per ambienti iframe che bloccano download) */}
      {pdfUrl && (
        <div className="print-hide fixed inset-0 z-[70] bg-slate-900/90 flex flex-col" data-testid="report-pdf-viewer">
          <div className="bg-slate-900 text-white px-4 py-2 flex items-center justify-between border-b border-slate-700">
            <div className="text-sm font-semibold">PDF generato — usa l'icona ⬇ del visualizzatore per scaricare</div>
            <div className="flex items-center gap-2">
              <a
                href={pdfUrl}
                download={`Report_Forfettario_${(clientName || 'cliente').replace(/[^a-zA-Z0-9_-]/g, '_')}_${year}.pdf`}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-3 py-1.5 rounded inline-flex items-center gap-1"
                data-testid="report-pdf-direct-download"
              >⬇ Scarica</a>
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-slate-600 hover:bg-slate-700 text-white text-sm font-semibold px-3 py-1.5 rounded"
                data-testid="report-pdf-open-tab"
              >↗ Apri in nuova scheda</a>
              <Button variant="ghost" onClick={closeViewer} className="text-white hover:bg-white/10">
                <X className="h-4 w-4 mr-1" /> Chiudi
              </Button>
            </div>
          </div>
          <iframe
            src={pdfUrl}
            title="PDF Report Forfettario"
            className="flex-1 w-full bg-white"
            style={{ border: 'none' }}
          />
        </div>
      )}
    </>
  );
};

const ReportBody = ({
  clientName, consultantName, year, today, calc, scenarios, costRows,
  contabForf, contabSemp, compensoSrl, rimborsoKmSrl, costiAmminSrl,
  incassato, totalCostsAnnual,
}) => {
  const filledCosts = costRows.filter(r => r.label && (parseFloat(r.value) || 0) > 0);

  // Calcoli narrativi
  const aliqEff = calc?.totale_anno?.totale_versare && incassato > 0
    ? ((calc.totale_anno.totale_versare / incassato) * 100).toFixed(1).replace('.', ',')
    : null;
  const nettoStimato = incassato - (calc?.totale_anno?.totale_versare || 0) - totalCostsAnnual;
  const cassaPercentuale = filledCosts.length > 0 && incassato > 0
    ? ((totalCostsAnnual / incassato) * 100).toFixed(1).replace('.', ',')
    : null;

  const sempleNetto = scenarios?.ordinario_semplificata?.netto_in_tasca;
  const forfNetto = scenarios?.forfettario?.netto_in_tasca;
  const deltaSemple = (sempleNetto != null && forfNetto != null) ? (sempleNetto - forfNetto) : null;

  return (
    <div style={{ padding: '14mm 14mm' }}>
      {/* HEADER */}
      <div className="print-no-break" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '18px', paddingBottom: '12px', borderBottom: '3px solid #6d28d9' }}>
        <div>
          <div style={{ fontSize: '9pt', color: '#7c3aed', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase' }}>Pianificazione fiscale</div>
          <h1>Report Forfettario {year}</h1>
          <div style={{ fontSize: '12pt', color: '#0f172a', marginTop: '6px', fontWeight: 700 }}>Cliente: {clientName}</div>
        </div>
        <div style={{ textAlign: 'right', fontSize: '8.5pt', color: '#64748b' }}>
          {consultantName && (
            <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '10pt' }}>{consultantName}</div>
          )}
          <div style={{ fontSize: '9pt', color: '#7c3aed', fontWeight: 600 }}>Consulente fiscale</div>
          <div style={{ marginTop: '4px' }}>Data emissione: {today}</div>
          <div>Anno fiscale: {year}</div>
        </div>
      </div>

      {/* NARRATIVA INTRODUTTIVA */}
      <section className="report-section print-no-break">
        <h2>In breve</h2>
        <div className="narrative">
          <p>
            Gentile <strong>{clientName}</strong>, ho preparato per Lei la pianificazione fiscale per l'anno <strong>{year}</strong>.
            Con un incassato annuo previsto di <strong>{fmt(incassato)}</strong>, applicando il coefficiente di redditività del <strong>{Math.round((calc.reddito.coefficiente_redditivita || 0) * 100)}%</strong>{calc.reddito.ateco_group ? ` (${calc.reddito.ateco_group})` : ''}, il reddito imponibile risulta di <strong>{fmt(calc.reddito.reddito_imponibile)}</strong>.
          </p>
          <p>
            Tra <strong>imposta sostitutiva</strong> ({fmt(calc.imposta.imposta_sostitutiva)}) e <strong>contributi previdenziali</strong> ({fmt(calc.contributi.totale_anno)}),
            l'esborso totale stimato per il {year} è di <strong>{fmt(calc.totale_anno.totale_versare)}</strong>{aliqEff ? `, pari al ${aliqEff}% del Suo fatturato.` : '.'}
          </p>
          {cassaPercentuale && (
            <p>
              I costi reali sostenuti per la Sua attività (affitto, materiali, utenze, ecc.) ammontano a circa <strong>{fmt(totalCostsAnnual)}</strong> (<strong>{cassaPercentuale}%</strong> del fatturato): nel regime forfettario <strong>non sono fiscalmente deducibili</strong>, ma escono comunque dalla Sua cassa.
              Il netto effettivamente disponibile dopo tasse, contributi e spese di gestione è stimato in <strong>{fmt(Math.max(0, nettoStimato))}</strong>.
            </p>
          )}
        </div>
      </section>

      {/* CALCOLO PRINCIPALE FORFETTARIO */}
      <section className="report-section print-no-break">
        <h2>1. Calcolo imposta sostitutiva Forfettario</h2>
        <table className="report-table">
          <tbody>
            <tr><td>Incassato annuo</td><td>{fmt(calc.reddito.incassato)}</td></tr>
            <tr><td style={{ paddingLeft: '24px', color: '#64748b' }}>× Coef. redditività {calc.reddito.ateco_group || ''}</td><td>{pct(calc.reddito.coefficiente_redditivita)}</td></tr>
            <tr className="subtotal"><td>= Reddito lordo forfettario</td><td>{fmt(calc.reddito.reddito_lordo_forfettario)}</td></tr>
            <tr className="deduction"><td>− Contributi previdenziali dedotti</td><td>− {fmt(calc.reddito.contributi_pagati_dedotti)}</td></tr>
            <tr className="subtotal"><td><strong>= Reddito imponibile</strong></td><td><strong>{fmt(calc.reddito.reddito_imponibile)}</strong></td></tr>
            <tr><td>Imposta sostitutiva ({pct(calc.imposta.aliquota)})</td><td>{fmt(calc.imposta.imposta_sostitutiva)}</td></tr>
            {calc.imposta.acconti_versati_anno > 0 && (
              <tr className="deduction"><td>− Acconti già versati</td><td>− {fmt(calc.imposta.acconti_versati_anno)}</td></tr>
            )}
            <tr className="total"><td>SALDO DA VERSARE</td><td>{fmt(calc.imposta.saldo_dovuto)}</td></tr>
          </tbody>
        </table>
        <p style={{ fontSize: '9pt', color: '#475569', marginTop: '10px', lineHeight: 1.5 }}>
          <strong>Come si legge.</strong> Nel regime forfettario il reddito imponibile non si calcola "fatturato meno costi", ma si applica un <strong>coefficiente fisso</strong> al fatturato in base alla tipologia di attività ATECO. Sul risultato si pagano una <strong>sola imposta sostitutiva</strong> (5% o 15%) e i contributi previdenziali, che sono completamente deducibili dal reddito imponibile.
        </p>
      </section>

      {/* CONTRIBUTI PREVIDENZIALI */}
      <section className="report-section print-no-break">
        <h2>2. Contributi previdenziali</h2>
        <div style={{ fontSize: '9pt', color: '#64748b', fontStyle: 'italic', marginBottom: '8px' }}>{calc.contributi.metodo}</div>
        <table className="report-table">
          <tbody>
            {calc.contributi.minimale_dovuto > 0 && (
              <tr><td>Minimale fisso INPS</td><td>{fmt(calc.contributi.minimale_dovuto)}</td></tr>
            )}
            {calc.contributi.eccedente_dovuto > 0 && (
              <tr><td>Eccedente sopra minimale</td><td>{fmt(calc.contributi.eccedente_dovuto)}</td></tr>
            )}
            {calc.contributi.addizionale_oltre_soglia > 0 && (
              <tr><td>IVS aggiuntivo 1% sopra soglia</td><td>{fmt(calc.contributi.addizionale_oltre_soglia)}</td></tr>
            )}
            <tr className="total"><td>Totale contributi anno {year}</td><td>{fmt(calc.contributi.totale_anno)}</td></tr>
          </tbody>
        </table>
        {calc.contributi.note && (
          <div style={{ fontSize: '8.5pt', color: '#64748b', fontStyle: 'italic', marginTop: '8px' }}>ℹ️ {calc.contributi.note}</div>
        )}
      </section>

      {/* TOTALE DA VERSARE */}
      <section className="report-section print-no-break" style={{ background: 'linear-gradient(135deg, #ede9fe 0%, #dbeafe 100%)', padding: '14px 18px', borderRadius: '8px', border: '1px solid #c4b5fd' }}>
        <div style={{ fontSize: '9pt', color: '#5b21b6', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Totale da versare nell'anno {year}</div>
        <div style={{ fontSize: '26pt', fontWeight: 800, color: '#4c1d95', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{fmt(calc.totale_anno.totale_versare)}</div>
        <div style={{ fontSize: '9pt', color: '#6d28d9', marginTop: '6px' }}>
          Imposta {fmt(calc.totale_anno.imposta)} + Contributi {fmt(calc.totale_anno.contributi)}
        </div>
      </section>

      {/* SCADENZE F24 */}
      {calc.f24 && calc.f24.length > 0 && (
        <section className="report-section print-no-break">
          <h2>3. Calendario versamenti F24</h2>
          <table className="report-table">
            <thead>
              <tr style={{ background: '#f1f5f9' }}>
                <td style={{ fontWeight: 700, fontSize: '8.5pt', color: '#475569', textTransform: 'uppercase' }}>Descrizione</td>
                <td style={{ fontWeight: 700, fontSize: '8.5pt', color: '#475569', textTransform: 'uppercase', width: '110px' }}>Scadenza</td>
                <td style={{ fontWeight: 700, fontSize: '8.5pt', color: '#475569', textTransform: 'uppercase', textAlign: 'right', width: '120px' }}>Importo</td>
              </tr>
            </thead>
            <tbody>
              {calc.f24.map((row, i) => (
                <tr key={i}>
                  <td>{row.descrizione}</td>
                  <td>{row.scadenza}</td>
                  <td>{fmt2(row.importo)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ fontSize: '9pt', color: '#475569', marginTop: '10px', lineHeight: 1.5 }}>
            <strong>Pianifichi i versamenti.</strong> Le scadenze sopra indicate sono obbligatorie: il consiglio è di accantonare mensilmente una quota proporzionale (circa il <strong>{aliqEff || '30'}%</strong> di ogni incasso) per arrivare preparato alle scadenze ed evitare di trovarsi senza liquidità.
          </p>
        </section>
      )}

      {/* CONFRONTO REGIMI (se calcolato) */}
      {scenarios && (
        <>
          <div className="print-page-break"></div>
          <section className="report-section">
            <h2>4. Confronto regimi: Forfettario vs Semplificato vs SRL</h2>
            <p style={{ fontSize: '9.5pt', color: '#475569', marginTop: '0', marginBottom: '12px', lineHeight: 1.5 }}>
              Analisi comparativa basata su un fatturato di <strong>{fmt(incassato)}</strong> e spese annue di <strong>{fmt(totalCostsAnnual)}</strong>.
              L'obiettivo è capire quale forma fiscale Le permette di tenere in tasca <strong>più soldi a parità di lavoro</strong>.
            </p>

            {/* Costi sostenuti */}
            {filledCosts.length > 0 && (
              <div className="print-no-break" style={{ marginBottom: '16px' }}>
                <h3>Dettaglio costi sostenuti</h3>
                <table className="report-table">
                  <tbody>
                    {filledCosts.map((r, i) => {
                      const v = parseFloat(r.value) || 0;
                      const isPct = r.mode === 'pct';
                      const baseVal = isPct ? (incassato * v / 100) : v;
                      const annualized = r.periodicity === 'monthly' ? baseVal * 12 : baseVal;
                      return (
                        <tr key={i}>
                          <td>{r.label}</td>
                          <td style={{ color: '#64748b', fontSize: '8.5pt' }}>
                            {isPct ? `${v}% del fatturato` : `${fmt(v)}`} · {r.periodicity === 'monthly' ? 'mensile' : 'annuo'}
                          </td>
                          <td>{fmt(annualized)}</td>
                        </tr>
                      );
                    })}
                    <tr className="total"><td colSpan="2">Totale costi annui</td><td>{fmt(totalCostsAnnual)}</td></tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Verdict */}
            <div className="print-no-break" style={{ background: '#ecfdf5', border: '2px solid #10b981', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
              <div style={{ fontSize: '8.5pt', color: '#065f46', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 700 }}>Verdetto numerico</div>
              <div style={{ fontSize: '15pt', fontWeight: 800, color: '#064e3b', marginTop: '4px' }}>
                Il regime con il netto più alto è: {scenarios.migliore === 'ordinario_semplificata' ? 'SEMPLIFICATO' : scenarios.migliore.toUpperCase()}
              </div>
              {scenarios.migliore === 'forfettario' && (
                <div style={{ fontSize: '9.5pt', color: '#047857', marginTop: '4px' }}>
                  Differenza vs Semplificato: <strong>{fmt(Math.abs(scenarios.risparmio_vs_forfettario.ordinario_semplificata))}</strong> · vs SRL: <strong>{fmt(Math.abs(scenarios.risparmio_vs_forfettario.srl))}</strong>
                </div>
              )}
              {scenarios.migliore !== 'forfettario' && (
                <div style={{ fontSize: '9.5pt', color: '#047857', marginTop: '4px' }}>
                  Risparmio: <strong>{fmt(Math.abs(scenarios.risparmio_vs_forfettario[scenarios.migliore]))}/anno</strong> rispetto al Forfettario.
                </div>
              )}
            </div>

            {/* 3 scenari in colonna con margini */}
            <div className="print-no-break" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', margin: '0 4px' }}>
              <ScenarioPrintCard s={scenarios.forfettario} winner={scenarios.migliore === 'forfettario'} badgeColor="violet" />
              <ScenarioPrintCard s={scenarios.ordinario_semplificata} winner={scenarios.migliore === 'ordinario_semplificata'} badgeColor="blue" />
              <ScenarioPrintCard s={scenarios.srl} winner={scenarios.migliore === 'srl'} badgeColor="emerald" />
            </div>

            {/* Narrativa di confronto: perché valutare il Semplificato */}
            <div className="narrative print-no-break" style={{ marginTop: '16px', background: '#eff6ff', borderLeftColor: '#1e40af' }}>
              <p>
                <strong>Perché valutare anche il Semplificato?</strong> Il Forfettario è semplice e con aliquota fissa, ma "pesa" sui costi reali perché non li deduce.
                Nel regime <strong>Semplificato</strong>, invece, ogni euro di spesa (affitto, materiali, pubblicità, formazione, attrezzature, viaggi…) <strong>abbatte direttamente il reddito imponibile</strong>: meno tasse pagate, più liquidità in cassa.
              </p>
              <p>
                Inoltre, nel Semplificato Lei può <strong>scaricare l'IVA</strong> sugli acquisti, dedurre <strong>ammortamenti</strong> sui beni strumentali e usufruire delle <strong>detrazioni IRPEF</strong> per spese personali (mediche, ristrutturazioni, figli a carico, mutui prima casa). Tutto questo nel Forfettario è precluso.
              </p>
              {deltaSemple != null && deltaSemple < 0 && (
                <p>
                  Nei numeri attuali il Forfettario "vince" di <strong>{fmt(Math.abs(deltaSemple))}</strong>, ma il gap si chiude rapidamente quando i costi reali aumentano o quando l'attività cresce: a quel punto il Semplificato diventa più vantaggioso e <strong>permette di crescere senza il tetto degli 85.000 €</strong> del Forfettario.
                </p>
              )}
              {deltaSemple != null && deltaSemple >= 0 && (
                <p>
                  Nel Suo caso il <strong>Semplificato è già più conveniente</strong> per <strong>{fmt(deltaSemple)}</strong>. La sceglierei senza esitazione: avrebbe più liquidità netta, niente tetto di fatturato e tutta la flessibilità delle detrazioni personali.
                </p>
              )}
            </div>

            {/* Break-even */}
            {scenarios.break_even && (
              <div className="print-no-break" style={{ marginTop: '16px', border: '2px solid #f59e0b', borderRadius: '8px', padding: '14px', background: '#fffbeb' }}>
                <h3 style={{ color: '#92400e' }}>Punti di pareggio Forfettario ↔ Semplificato</h3>
                <p style={{ fontSize: '9pt', color: '#78716c', marginTop: '4px', marginBottom: '10px' }}>
                  Le soglie esatte oltre cui cambia la convenienza, partendo dai Suoi dati attuali.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div>
                    <div style={{ fontSize: '8pt', color: '#b45309', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px' }}>
                      A parità di fatturato ({fmt(scenarios.break_even.fatturato_riferimento)})
                    </div>
                    {scenarios.break_even.costi_breakeven != null ? (
                      <>
                        <div style={{ fontSize: '9pt', color: '#475569', marginTop: '4px' }}>Limite spese per restare nel Forfettario:</div>
                        <div style={{ fontSize: '18pt', fontWeight: 800, color: '#b45309', fontVariantNumeric: 'tabular-nums', marginTop: '2px' }}>
                          {fmt(scenarios.break_even.costi_breakeven)}
                        </div>
                      </>
                    ) : (
                      <div style={{ fontSize: '9.5pt', color: '#047857', fontStyle: 'italic', marginTop: '4px' }}>
                        ✓ Forfettario sempre più conveniente.
                      </div>
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: '8pt', color: '#b45309', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px' }}>
                      A parità di spese ({fmt(scenarios.break_even.spese_riferimento)})
                    </div>
                    {scenarios.break_even.fatturato_breakeven != null ? (
                      <>
                        <div style={{ fontSize: '9pt', color: '#475569', marginTop: '4px' }}>Soglia minima fatturato per il Forfettario:</div>
                        <div style={{ fontSize: '18pt', fontWeight: 800, color: '#b45309', fontVariantNumeric: 'tabular-nums', marginTop: '2px' }}>
                          {fmt(scenarios.break_even.fatturato_breakeven)}
                        </div>
                      </>
                    ) : (
                      <div style={{ fontSize: '9.5pt', color: '#047857', fontStyle: 'italic', marginTop: '4px' }}>
                        ✓ Forfettario conviene a qualunque livello di fatturato (fino a {fmt(scenarios.break_even.limite_forfettario)}).
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ fontSize: '8pt', color: '#78716c', fontStyle: 'italic', marginTop: '10px' }}>
                  ⚖️ Il calcolo tiene già conto del maggior costo della contabilità: Semplificato {fmt(contabSemp)} vs Forfettario {fmt(contabForf)} €/anno.
                </div>
              </div>
            )}

            {/* Parametri SRL utilizzati */}
            <div className="print-no-break" style={{ marginTop: '16px', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '12px 14px', background: '#f8fafc' }}>
              <h3 style={{ color: '#475569', fontSize: '9.5pt' }}>Parametri SRL utilizzati nella simulazione</h3>
              <table className="report-table" style={{ fontSize: '9pt', marginTop: '4px' }}>
                <tbody>
                  <tr><td>Compenso amministratore</td><td>{fmt(compensoSrl)}/mese</td></tr>
                  <tr><td>Rimborso km annuo</td><td>{fmt(rimborsoKmSrl)}</td></tr>
                  <tr><td>Costi amministrativi (commercialista + paghe + diritti CCIAA)</td><td>{fmt(costiAmminSrl)}</td></tr>
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {/* FOOTER */}
      <div style={{ marginTop: '24px', paddingTop: '12px', borderTop: '1px solid #e2e8f0', fontSize: '8pt', color: '#64748b', textAlign: 'center', fontStyle: 'italic', lineHeight: 1.5 }}>
        Documento elaborato da <strong>{consultantName || 'ControlloGestione'}</strong> il {today}.
        I valori sono stime basate sui dati forniti e sulla normativa fiscale vigente per l'anno {year}.
        Le aliquote contributive e i coefficienti di redditività potrebbero variare in base alle disposizioni INPS e Agenzia delle Entrate.
        Per qualunque chiarimento può contattare direttamente il Suo consulente.
      </div>
    </div>
  );
};

const ScenarioPrintCard = ({ s, winner, badgeColor }) => {
  const incassato = s.incassato || 0;
  const isForfettario = s.imposta_sostitutiva != null;
  const isSemplificato = s.irpef_lorda != null;
  const isSrl = s.ires != null;

  return (
    <div className={`scenario-card ${winner ? 'winner' : ''}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <div className={`badge badge-${badgeColor}`} style={{ fontSize: '7.5pt' }}>{s.regime}</div>
        {winner && <div className="badge badge-winner" style={{ fontSize: '7.5pt' }}>MIGLIORE</div>}
      </div>
      <table className="report-table" style={{ fontSize: '8.5pt' }}>
        <tbody>
          <tr><td>Incassato</td><td>{fmt(s.incassato)}</td></tr>

          {isForfettario && (
            <>
              <tr><td style={{ paddingLeft: '12px', color: '#64748b', fontSize: '8pt' }}>× {Math.round((s.coefficiente_redditivita || 0) * 100)}% (coef.)</td><td></td></tr>
              <tr><td>= Reddito lordo</td><td>{fmt(s.reddito_lordo)}</td></tr>
              <tr className="deduction"><td>− Contributi dedotti</td><td>− {fmt(s.contributi_dedotti)}</td></tr>
              <tr className="subtotal"><td>= Reddito imponibile</td><td>{fmt(s.reddito_imponibile)}</td></tr>
              <tr><td>Imposta sostitutiva ({Math.round((s.aliquota_imposta || 0) * 100)}%)</td><td>{fmt(s.imposta_sostitutiva)}</td></tr>
            </>
          )}

          {isSemplificato && (
            <>
              <tr><td>− Costi deducibili</td><td>− {fmt(s.costi_deducibili)}</td></tr>
              <tr><td>= Reddito lordo</td><td>{fmt(s.reddito_lordo)}</td></tr>
              <tr><td>− Contributi dedotti</td><td>− {fmt(s.contributi_dedotti)}</td></tr>
              <tr className="subtotal"><td>= Reddito imponibile</td><td>{fmt(s.reddito_imponibile)}</td></tr>
              <tr><td>IRPEF lorda (scaglioni)</td><td>{fmt(s.irpef_lorda)}</td></tr>
              {s.detrazione_lavoro_autonomo > 0 && (
                <tr className="deduction"><td>− Detrazione lavoro autonomo</td><td>− {fmt(s.detrazione_lavoro_autonomo)}</td></tr>
              )}
              <tr><td>= IRPEF netta</td><td>{fmt(s.irpef_netta)}</td></tr>
              <tr><td>+ Addiz. locali ({(s.addizionali_locali_pct ?? 3).toString().replace('.', ',')}%)</td><td>{fmt(s.addizionali_locali)}</td></tr>
            </>
          )}

          {isSrl && (
            <>
              <tr><td>− Costi deducibili</td><td>− {fmt(s.costi_deducibili)}</td></tr>
              {s.compensi_amministratori_annui > 0 && (
                <tr><td>− Compenso amm. annuo</td><td>− {fmt(s.compensi_amministratori_annui)}</td></tr>
              )}
              {s.inps_azienda_annua > 0 && (
                <tr><td>− INPS quota azienda</td><td>− {fmt(s.inps_azienda_annua)}</td></tr>
              )}
              {s.rimborsi_km_totali > 0 && (
                <tr><td>− Rimborso km</td><td>− {fmt(s.rimborsi_km_totali)}</td></tr>
              )}
              {s.costi_amministrativi_srl_detail > 0 && (
                <tr><td>− Costi amministrativi</td><td>− {fmt(s.costi_amministrativi_srl_detail)}</td></tr>
              )}
              <tr className="subtotal"><td>= Utile ante imposte</td><td>{fmt(s.utile_ante_imposte_srl)}</td></tr>
              <tr><td>IRES (24%)</td><td>{fmt(s.ires)}</td></tr>
              <tr><td>IRAP (3,9%)</td><td>{fmt(s.irap)}</td></tr>
              <tr><td>IRPEF amministratore</td><td>{fmt(s.irpef_amministratori)}</td></tr>
            </>
          )}

          <tr><td>Contributi previdenziali</td><td>{fmt(s.contributi_dovuti)}</td></tr>
          <tr className="total"><td>Imposte + contributi</td><td>{fmt(s.totale_imposte_contributi)}</td></tr>
          {s.costo_contabilita > 0 && (
            <tr><td style={{ color: '#dc2626', fontSize: '8pt' }}>+ Costo contabilità</td><td style={{ color: '#dc2626' }}>{fmt(s.costo_contabilita)}</td></tr>
          )}
          {s.costi_reali_sostenuti > 0 && (
            <tr style={{ background: isForfettario ? '#fef2f2' : '#f8fafc' }}>
              <td style={{ color: '#dc2626', fontSize: '8pt' }}>
                {isForfettario
                  ? '− Costi reali (non deducibili, escono dalla cassa)'
                  : '− Costi reali (già dedotti, escono dalla cassa)'}
              </td>
              <td style={{ color: '#dc2626', fontWeight: 600 }}>− {fmt(s.costi_reali_sostenuti)}</td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="net-pocket">
        <div className="label">Netto in tasca</div>
        <div className="value">{fmt(s.netto_in_tasca)}</div>
        {incassato > 0 && (
          <div className="pct">= {((s.netto_in_tasca / incassato) * 100).toFixed(1)}% del fatturato · Aliq. eff. {s.aliquota_effettiva_pct}%</div>
        )}
      </div>
    </div>
  );
};
